import { Injectable, Injector } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { toQueryParams } from '@app/helpers/object.helpers';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { noticeError } from '@app/helpers/new-relic';
import { NzMessageService } from 'ng-zorro-antd';
import { path, prop } from '@ramda';
import { HttpStatusCode } from '@modules/shared/constants/http.enum';

export function handleApiError(error: HttpErrorResponse, nzMessage: NzMessageService, requestDetails?: any) {
    if (requestDetails) {
        noticeError({
            message: 'API Request Failed',
            error,
            request: requestDetails,
        });
    }

    const handledErrors = [HttpStatusCode.BadRequest, HttpStatusCode.NotFound];
    const defaultMessage = 'Hubo un error el proceso.';

    if (!handledErrors.includes(prop('status', error))) {
        const errorMessage = path(['error', 'message'], error) || prop('message', error) || defaultMessage;
        nzMessage.error(errorMessage);
    }
    return throwError(error);
}

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private railsApiUrl = environment.baseUrlRails;

    protected http: HttpClient;
    private readonly nzMessage: NzMessageService;

    constructor(protected injector: Injector) {
        this.http = injector.get(HttpClient);
        this.nzMessage = injector.get(NzMessageService);
    }

    protected get<T>(endpoint: string, params: Record<string, any> = {}) {
        const queryParams = Object.keys(params).length ? toQueryParams(params) : '';
        const url = `${this.railsApiUrl}${endpoint}${queryParams}`;
        return this.http.get<T>(url).pipe(
            catchError((error) =>
                handleApiError(error, this.nzMessage, {
                    method: 'GET',
                    url,
                    params,
                })
            )
        );
    }

    // POST request
    protected post<T>(endpoint: string, body: any, headers?: HttpHeaders): Observable<T> {
        const url = `${this.railsApiUrl}${endpoint}`;
        return this.http.post<T>(url, body, { headers }).pipe(
            catchError((error) =>
                handleApiError(error, this.nzMessage, {
                    method: 'POST',
                    url,
                    body,
                })
            )
        );
    }

    // PUT request
    protected put<T>(endpoint: string, body: any, headers?: HttpHeaders): Observable<T> {
        return this.http.put<T>(`${this.railsApiUrl}${endpoint}`, body, { headers });
    }

    // DELETE request
    protected delete<T>(endpoint: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
        const url = `${this.railsApiUrl}${endpoint}`;
        return this.http.delete<T>(`${this.railsApiUrl}${endpoint}`, { params, headers }).pipe(
            catchError((error) =>
                handleApiError(error, this.nzMessage, {
                    method: 'DELETE',
                    url,
                })
            )
        );
    }

    // PATCH request
    protected patch<T>(endpoint: string, body: any, headers?: HttpHeaders): Observable<T> {
        return this.http.patch<T>(`${this.railsApiUrl}${endpoint}`, body, { headers });
    }
}
